import { useMemo, useState } from 'react';
import { USD, fullDate, magnitude } from '../../lib/format';
import { C, glass, ink } from '../../lib/theme';
import type { CalendarRow } from '../../lib/types';
import { ChartEmpty } from './shared';

const WEEKDAYS = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];

/**
 * Gráfico 7 — Calendário de perdas (PRD §9.7), heatmap estilo GitHub. Revela o
 * padrão que nenhum outro gráfico mostra: picos ligados a data de cobrança
 * ("todo dia 5 tem pico de churn" é acionável). Clique num dia filtra a lista.
 */
export function Calendario({ rows, onPick }: { rows: CalendarRow[]; onPick?: (day: string) => void }) {
  const [hover, setHover] = useState<string | null>(null);

  const { weeks, max, monthMarks } = useMemo(() => {
    const byDay = new Map<string, { value: number; count: number }>();
    let m = 0;
    for (const r of rows) {
      const day = String(r.day).slice(0, 10);
      const v = magnitude(r.value);
      byDay.set(day, { value: v, count: Number(r.count ?? 0) });
      if (v > m) m = v;
    }

    const days = [...byDay.keys()].sort();
    if (days.length === 0) return { weeks: [], max: 1, monthMarks: [] };

    const start = new Date(`${days[0]}T00:00:00Z`);
    const end = new Date(`${days[days.length - 1]}T00:00:00Z`);
    // Recua até a segunda-feira da primeira semana pra a grade alinhar por dia da semana.
    const cursor = new Date(start);
    cursor.setUTCDate(cursor.getUTCDate() - ((cursor.getUTCDay() + 6) % 7));

    const cols: Array<Array<{ day: string; value: number; count: number } | null>> = [];
    const marks: Array<{ col: number; label: string }> = [];
    let lastMonth = -1;

    while (cursor <= end) {
      const col: Array<{ day: string; value: number; count: number } | null> = [];
      for (let i = 0; i < 7; i++) {
        const iso = cursor.toISOString().slice(0, 10);
        const hit = byDay.get(iso);
        const inRange = cursor >= start && cursor <= end;
        col.push(inRange ? { day: iso, value: hit?.value ?? 0, count: hit?.count ?? 0 } : null);
        if (i === 0 && cursor.getUTCMonth() !== lastMonth) {
          lastMonth = cursor.getUTCMonth();
          marks.push({ col: cols.length, label: cursor.toLocaleDateString('pt-BR', { month: 'short', timeZone: 'UTC' }) });
        }
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }
      cols.push(col);
    }
    return { weeks: cols, max: m || 1, monthMarks: marks };
  }, [rows]);

  if (rows.length === 0 || weeks.length === 0) return <ChartEmpty />;

  const hovered = hover ? weeks.flat().find((d) => d?.day === hover) : null;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto', position: 'relative' }}>
      <div style={{ display: 'flex', gap: 3, paddingLeft: 20, marginBottom: 4, minWidth: 'min-content' }}>
        {weeks.map((_, i) => {
          const mark = monthMarks.find((m) => m.col === i);
          return (
            <div key={i} style={{ width: 13, fontSize: 9, color: ink(0.35), textTransform: 'capitalize', flex: 'none' }}>
              {mark ? mark.label : ''}
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 3, minWidth: 'min-content' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 'none', width: 17 }}>
          {WEEKDAYS.map((w, i) => (
            <div key={i} style={{ height: 13, fontSize: 8.5, color: ink(0.28), lineHeight: '13px' }}>{i % 2 === 0 ? w : ''}</div>
          ))}
        </div>
        {weeks.map((col, ci) => (
          <div key={ci} style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 'none' }}>
            {col.map((cell, ri) => {
              if (!cell) return <div key={ri} style={{ width: 13, height: 13 }} />;
              const intensity = cell.value > 0 ? Math.sqrt(cell.value / max) : 0;
              const dim = hover !== null && hover !== cell.day;
              return (
                <div
                  key={ri}
                  onMouseEnter={() => setHover(cell.day)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => cell.value > 0 && onPick?.(cell.day)}
                  style={{
                    width: 13, height: 13, borderRadius: 3,
                    background: cell.value > 0
                      ? `rgba(255,107,71,${(0.14 + intensity * 0.76).toFixed(3)})`
                      : ink(0.05),
                    border: hover === cell.day ? `1px solid ${C.accent}` : '1px solid transparent',
                    cursor: cell.value > 0 && onPick ? 'pointer' : 'default',
                    opacity: dim ? 0.4 : 1,
                    transition: 'opacity 0.15s ease',
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>

      {hovered && (
        <div style={{
          ...glass, borderRadius: 11, padding: '9px 12px', marginTop: 12,
          alignSelf: 'flex-start', minWidth: 190,
        }}>
          <div style={{ fontSize: 11, color: ink(0.5), marginBottom: 3 }}>{fullDate(hovered.day)}</div>
          <div style={{ fontSize: 13.5, fontWeight: 750, color: hovered.value > 0 ? C.accent : ink(0.4) }}>
            {hovered.value > 0 ? `${USD(hovered.value)} · ${hovered.count} evento${hovered.count === 1 ? '' : 's'}` : 'sem perda'}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 'auto', paddingTop: 12, fontSize: 10, color: ink(0.3) }}>
        menos
        {[0.05, 0.3, 0.5, 0.72, 0.9].map((a, i) => (
          <span key={i} style={{
            width: 11, height: 11, borderRadius: 3,
            background: i === 0 ? ink(0.05) : `rgba(255,107,71,${a})`,
          }} />
        ))}
        mais · clique num dia filtra a lista
      </div>
    </div>
  );
}
